import { useEffect, useState } from 'react';
import { Layers } from 'lucide-react';
import { api, ApiError } from '@/services/api';
import type { DeploymentLog } from '@/types';

export function DeploymentsTab({ siteId }: { siteId: string }) {
  const [logs, setLogs] = useState<DeploymentLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get<DeploymentLog[]>(`/sites/${siteId}/deployments`)
      .then(setLogs)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 404) {
          setLogs([]);
        } else {
          setError(true);
        }
      })
      .finally(() => setLoading(false));
  }, [siteId]);

  if (loading) {
    return <div className="flex h-32 items-center justify-center"><div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" /></div>;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center rounded-xl border border-surface-300 bg-surface-100 py-12">
        <Layers className="mb-2 h-8 w-8 text-neutral-600" />
        <p className="text-sm text-neutral-500">Deployment-Logs konnten nicht geladen werden.</p>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="flex flex-col items-center rounded-xl border border-surface-300 bg-surface-100 py-12">
        <Layers className="mb-2 h-8 w-8 text-neutral-600" />
        <p className="text-sm text-neutral-500">Noch keine Deployments vorhanden.</p>
      </div>
    );
  }

  const statusColors: Record<string, string> = {
    SUCCESS: 'text-green-400',
    BUILDING: 'text-yellow-400',
    FAILED: 'text-red-400',
  };

  return (
    <div className="overflow-x-auto rounded-xl border border-surface-300 bg-surface-100">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-surface-300 text-xs uppercase text-neutral-500">
            <th className="px-5 py-3 font-medium">Status</th>
            <th className="px-5 py-3 font-medium">Image</th>
            <th className="px-5 py-3 font-medium">Erstellt</th>
            <th className="px-5 py-3 font-medium">Dauer</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-surface-300">
          {logs.map((log) => (
            <tr key={log.id} className="hover:bg-surface-200/50 transition-colors">
              <td className={`px-5 py-3 font-medium ${statusColors[log.status] ?? 'text-neutral-400'}`}>
                {log.status}
              </td>
              <td className="px-5 py-3 text-neutral-400 text-xs font-mono">
                {log.imageTag}
              </td>
              <td className="px-5 py-3 text-neutral-400 text-xs">
                {new Date(log.createdAt).toLocaleString('de-DE')}
              </td>
              <td className="px-5 py-3 text-neutral-500 text-xs">
                {log.duration != null ? `${(log.duration / 1000).toFixed(1)}s` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
